
import Enumerable from "linq";
import { Component, Mixins, Prop, } from "vue-property-decorator";
import { ReceptionComment, ReceptionCommentSet } from "../../../../../domain/workflow/reception";
import { ReceptionCommentKinds, ReceptionCommentUnitKinds, ReceptionCommentKindsName, ReceptionCommentSortItemKinds } from "../../../../../domain/workflow/kinds";
import { ReceptionDateTranslator } from "../../../../../domain/workflow";
import CommentDialog from "../../../dialogs/CommentDialog.vue";
import { CommentListCommonMixin } from "../mixins";
import { ReceptionCommentDispInfo } from "../../../../../domain/workflow/reference";

@Component
export default class CommentsMixedListTableMixin extends Mixins(CommentListCommonMixin) {
  @Prop()
  isRegistered: boolean;

  @Prop()
  commentSet: ReceptionCommentSet;

  @Prop({default: false})
  isReadOnly: boolean;

  @Prop()
  isDeleted: boolean;

  @Prop()
  commentDialog: CommentDialog;

  _dateTranslator: ReceptionDateTranslator;

  created(): void {
    this._dateTranslator = new ReceptionDateTranslator();
  }

  mounted(): void {
    this.sortType = this.commentDefaultSortType;
  }

  data(): any {
    return {
      sortType: ReceptionCommentSortItemKinds.UpdateDateTime,
      sortTypeCreateDateTime: ReceptionCommentSortItemKinds.CreateDateTime,
      sortTypeUpdateDateTime: ReceptionCommentSortItemKinds.UpdateDateTime,
    };
  }
  sortType: ReceptionCommentSortItemKinds;
  sortTypeCreateDateTime: ReceptionCommentSortItemKinds;
  sortTypeUpdateDateTime: ReceptionCommentSortItemKinds;

  get comments(): ReceptionComment[] {
    return this.isAdminCommentTabEnable
      ? this.commentsForAdministrator
      : this.commentsForCreator;
  }

  get sortedComments(): ReceptionComment[] {
    return (this.sortType === ReceptionCommentSortItemKinds.UpdateDateTime)
      ? this.sortByUpdateDateTime(this.comments)
      : this.sortByCreateDateTime(this.comments);
  }

  get commentsForCreator(): ReceptionComment[] {
    if (!this.commentSet) { return []; }
    return this.commentSet.comments
      .concat(this.commentSet.nextMessages)
      .concat(this.commentSet.backMessages)
      .concat(this.commentSet.conflictComments);
  }

  get commentsForAdministrator(): ReceptionComment[] {
    if (!this.commentSet) { return []; }
    return this.commentsForCreator
      .concat(this.isRestrictCommentEnable ? this.commentSet.restrictComments : [])
      .concat(this.isContactedCommentEnable ? this.commentSet.contactedComments : [])
      .concat(this.isDelCommentEnable ? this.commentSet.delComments : []);
  }

  get isAddButtonEnable(): boolean {
    return this.detailId
      && !this.isCommentReadOnly
      && (this.isCommentAddible || this.isNextMessageAddible || this.isRestrictCommentAddible || this.isContactedCommentAddible || this.isDeleteCommentAddible);
  }

  get isCommentRegisterable(): boolean {
    return this.commentSetting && this.commentSetting.isCommentRegisterable;
  }

  get isCommentAddible(): boolean {
    return this.isRegistered
      && this.isCommentRegisterable
      && this.isAddible(ReceptionCommentKinds.Comment, this.commentUnitKind);
  }

  get isCommentReadOnly(): boolean {
    if (this.isReadOnly) { return true; }
    return this.commentSet ? this.commentSet.isReadOnly : false;
  }

  get isNextMessageRegisterable(): boolean {
    return this.commentSetting && this.commentSetting.isNextMessageRegisterable;
  }

  get isNextMessageAddible(): boolean {
    return this.isRegistered
      && this.isNextMessageRegisterable
      && this.isAddible(ReceptionCommentKinds.NextMessage, this.nextMessageUnitKind);
  }

  get isRestrictCommentRegisterable(): boolean {
    return this.commentSetting && this.commentSetting.isRestrictCommentRegisterable && this.isRestrictCommentEnable;
  }

  get isRestrictCommentAddible(): boolean {
    return this.isRegistered
      && this.isRestrictCommentRegisterable
      && this.isAdminCommentTabEnable
      && this.isAddible(ReceptionCommentKinds.Restrict, this.restrictCommentUnitKind);
  }

  get isContactedCommentRegisterable(): boolean {
    return this.commentSetting && this.commentSetting.isContactedCommentRegisterable && this.isContactedCommentEnable;
  }

  get isContactedCommentAddible(): boolean {
    return this.isRegistered
      && this.isContactedCommentRegisterable
      && this.isAdminCommentTabEnable
      && this.isAddible(ReceptionCommentKinds.ContactedInfo, this.contactedCommentUnitKind);
  }

  get isDeleteCommentEnable(): boolean {
    return this.commentSetting && this.commentSetting.isDelCommentEnable;
  }

  get isDeleteCommentRegisterable(): boolean {
    return this.commentSetting && this.commentSetting.isDelCommentRegisterable && this.isDeleteCommentEnable;
  }

  get isDeleteCommentAddible(): boolean {
    return this.isDeleted
      && this.isDeleteCommentRegisterable
      && this.isAdminCommentTabEnable
      && this.isAddible(ReceptionCommentKinds.Delete, this.deleteCommentUnitKind);
  }

  isAddible(commentKind: ReceptionCommentKinds, unitKind: ReceptionCommentUnitKinds): boolean {
    return ((unitKind === ReceptionCommentUnitKinds.Single && this.getCommentLength(commentKind) === 0)
      || unitKind === ReceptionCommentUnitKinds.Multiple);
  }

  get addibleCommentKinds(): ReceptionCommentKinds[] {
    return [this.isCommentAddible ? ReceptionCommentKinds.Comment : null]
      .concat(this.isNextMessageAddible ? ReceptionCommentKinds.NextMessage : null)
      .concat(this.isRestrictCommentAddible ? ReceptionCommentKinds.Restrict : null)
      .concat(this.isContactedCommentAddible ? ReceptionCommentKinds.ContactedInfo : null)
      .concat(this.isDeleteCommentAddible ? ReceptionCommentKinds.Delete : null);
  }

  isEditable(row: ReceptionComment): boolean {
    return !this.isCommentReadOnly && row.isEditable;
  }

  isDeletable(row: ReceptionComment): boolean {
    return !this.isCommentReadOnly && row.isDeletable;
  }

  getLastUpdateInformation(row: ReceptionComment): string {
    return row.createUser && row.updateDateTime
      ? "最終更新：" + row.createUser.name + "　" + this._dateTranslator.toDateTimeDispString(row.updateDateTime)
      : "";
  }

  toCommentKind(row: ReceptionComment): string {
    return this.getCommentLabel(ReceptionCommentKindsName.toKind(row.type));
  }

  toBackgroundColor(row: ReceptionComment): string {
    const defaultColor = "#B79F7C";
    const kind = ReceptionCommentKindsName.toKind(row.type);
    const setting = this.commentSetting ? this.commentSetting : new ReceptionCommentDispInfo();

    if (!setting.shouldShowCommentsMixed) { return defaultColor; }

    switch (kind) {
      case ReceptionCommentKinds.Comment:
        return this.isColor(setting.mixedCommentBackgroundColor) ? setting.mixedCommentBackgroundColor : defaultColor;
      case ReceptionCommentKinds.Restrict:
        return this.isColor(setting.mixedRestrictedBackgroundColor) ? setting.mixedRestrictedBackgroundColor : defaultColor;
      case ReceptionCommentKinds.NextMessage:
        return this.isColor(setting.mixedNextMessageBackgroundColor) ? setting.mixedNextMessageBackgroundColor : defaultColor;
      case ReceptionCommentKinds.BackMessage:
        return this.isColor(setting.mixedBackMessageBackgroundColor) ? setting.mixedBackMessageBackgroundColor : defaultColor;
      case ReceptionCommentKinds.ContactedInfo:
        return this.isColor(setting.mixedContactedInfoBackgroundColor) ? setting.mixedContactedInfoBackgroundColor : defaultColor;
        case ReceptionCommentKinds.Delete:
        return this.isColor(setting.mixedDelCommentBackgroundColor) ? setting.mixedDelCommentBackgroundColor : defaultColor;
      case ReceptionCommentKinds.Conflict:
        return this.isColor(setting.mixedConflictBackgroundColor) ? setting.mixedConflictBackgroundColor : defaultColor;
      default:
        return defaultColor;
    }
  }

  isColor(color: string) {
    const elementForColorDiscrimination = document.createElement("span");
    elementForColorDiscrimination.style.backgroundColor = color;
    return elementForColorDiscrimination.style.backgroundColor !== "";
  }

  getCommentLength(kind: ReceptionCommentKinds): number {
    return Enumerable.from(this.comments ? this.comments : [])
      .where(c => ReceptionCommentKindsName.toKind(c.type) === kind)
      .toArray()
      .length;
  }

  editComment(row: ReceptionComment): void {
    const commentKind = ReceptionCommentKindsName.toKind(row.type);
    const commentSet = (commentKind === ReceptionCommentKinds.NextMessage || commentKind === ReceptionCommentKinds.BackMessage)
      ? this.commentSet
      : null;
    this.showCommentDialog(commentKind, this.getCommentLabel(commentKind) + "編集", row, commentSet);
  }

  deleteComment(row: ReceptionComment): void {
    this.deleteCommentCommon(ReceptionCommentKindsName.toKind(row.type), row);
  }

  sortByUpdateDateTime(comments: ReceptionComment[]): ReceptionComment[] {
    return this.shouldSortCommentAsc
      ? Enumerable.from(comments).orderBy(c => c.updateDateTime).toArray()
      : Enumerable.from(comments).orderByDescending(c => c.updateDateTime).toArray();
  }

  sortByCreateDateTime(comments: ReceptionComment[]): ReceptionComment[] {
    return this.shouldSortCommentAsc
      ? Enumerable.from(comments).orderBy(c => c.createDateTime).toArray()
      : Enumerable.from(comments).orderByDescending(c => c.createDateTime).toArray();
  }

  async showCommentDialog(kind: ReceptionCommentKinds, title: string, comment: ReceptionComment, commentSet: ReceptionCommentSet): Promise<void> {
    const result = await this.commentDialog.show(
      this.toCommentDialogCondition(
        title,
        (comment ? comment.content : ""),
        commentSet
      )
    );
    if (result.isConfirmed) {
      const receptionComment = comment ? comment : new ReceptionComment();
      receptionComment.content = result.note;
      this.registerComment(kind, comment);
    }
  }

  async deleteCommentCommon(kind: ReceptionCommentKinds, comment: ReceptionComment): Promise<void> {
    const confirmResult = await this.confirmDeleteComment(kind);
    if (confirmResult == null) { // 登録キャンセルした
      return;
    }
    this.execDeleteComment(kind, comment);
  }

  async registerNewComment(kind: ReceptionCommentKinds, comment: string): Promise<boolean> {
    const receptionComment = new ReceptionComment();
    receptionComment.content = comment;
    return this.registerComment(kind, receptionComment);
  }

  async confirmDeleteComment(kind: ReceptionCommentKinds): Promise<number> {
    return this.$commonDialogs.confirmation.show({
      title: "削除確認",
      message: [this.getCommentLabel(kind) + "を削除してもよろしいですか？"],
      buttons: [
        {
          kind: "danger",
          label: "削除する",
        }
      ],
      cancellation: {
        isEnable: true,
        label: this.cancelButtonLabel,
      },
    });
  }
}
